import React from "react";
import { motion } from "framer-motion";

export default function Expertise({ setActiveTab }) {
  const expertise = [
    {
      id: 1,
      icon: "🛒",
      title: "Shopify Development & Maintenance",
      description: "Complete Shopify store development and ongoing maintenance for all Shopify plans. Custom theme development, app integrations, and regular maintenance to keep your store running smoothly.",
      features: ["Custom Theme Development", "App Integration & Setup", "Regular Maintenance", "Bug Fixes & Updates", "Feature Enhancements"]
    },
    {
      id: 2,
      icon: "🔧",
      title: "Checkout Extensibility & Shopify Functions",
      description: "Advanced Shopify Plus checkout customization using Functions and extensibility. Create custom payment flows, validation rules, and seamless checkout experiences.",
      features: ["Shopify Functions Development", "Custom Checkout Extensions", "Payment Flow Customization", "Validation & Business Rules", "Third-party Integrations"]
    },
    {
      id: 3,
      icon: "📈",
      title: "Conversion Rate Optimization (CRO)",
      description: "Increase your store's conversion rates through data-driven optimization. A/B testing, user experience improvements, and strategic checkout optimization.",
      features: ["A/B Testing & Analytics", "User Experience Optimization", "Checkout Flow Improvement", "Product Page Optimization", "Mobile Conversion Enhancement"]
    },
    {
      id: 4,
      icon: "🚀",
      title: "Site Speed Optimization",
      description: "Enhance your Shopify store's performance with advanced optimization techniques. From faster loading times and improved Core Web Vitals to a smoother user experience — all designed to boost engagement and drive more conversions.",
      features: ["Code Optimization", "Image Compression & Lazy Loading", "Theme Performance Tuning", "CDN Setup", "Core Web Vitals Improvement"]
    },
    {
      id: 5,
      icon: "🌐",
      title: "WordPress & CMS Development",
      description: "Full-service WordPress and CMS development including custom themes, plugins, and complex functionality. Expert in WooCommerce, and custom post types.",
      features: ["Custom WordPress Themes", "WooCommerce Development", "Plugin Development", "CMS Migration", "Headless CMS Setup"]
    },
    {
      id: 6,
      icon: "🎨",
      title: "Webflow & No-Code Development",
      description: "Professional Webflow development and no-code solutions. Create stunning, responsive websites with advanced interactions, CMS functionality, and seamless integrations.",
      features: ["Custom Webflow Sites", "Advanced Interactions", "CMS Collections", "Third-party Integrations", "Responsive Design"]
    },
    {
      id: 7,
      icon: "🔄",
      title: "Platform Migration to Shopify",
      description: "Seamless migration from any e-commerce platform to Shopify. Complete data preservation, SEO optimization, and modern store redesign for a fresh start.",
      features: ["Migration from Any Platform", "Complete Data Preservation", "SEO Migration & Optimization", "Modern Store Redesign", "Training & Support"]
    },
    {
      id: 8,
      icon: "⚡",
      title: "Custom App Development",
      description: "Build powerful custom Shopify apps tailored to your business needs. Private apps, public apps, and complex integrations with third-party systems and APIs.",
      features: ["Custom Private Apps", "Public App Development", "API Integrations", "Admin Dashboard Development", "Database Design & Management"]
    }
  ];

  const containerVariants = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    initial: { opacity: 0, y: 30 },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    initial: { rotate: 0 },
    hover: { 
      rotate: 360,
      scale: 1.2,
      transition: { duration: 0.6 }
    }
  };

  const featureVariants = {
    initial: { opacity: 0, x: -10 },
    animate: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="max-w-6xl mx-auto"
      variants={containerVariants}
      initial="initial"
      animate="animate"
    >
      <motion.div 
        className="text-center mb-8 sm:mb-12"
        variants={cardVariants}
      >
        <motion.h2 
          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Professional Expertise
        </motion.h2>
        <motion.p 
          className="text-sm sm:text-base md:text-lg text-gray-600"
          whileHover={{ color: "#374151" }}
          transition={{ duration: 0.3 }}
        >
          Comprehensive web development and e-commerce solutions across multiple platforms to grow your digital business
        </motion.p>
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
        variants={containerVariants}
      >
        {expertise.map((service) => (
          <motion.div
            key={service.id}
            className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow"
            variants={cardVariants}
            whileHover={{ 
              y: -5,
              scale: 1.02,
              transition: { duration: 0.3 }
            }}
          >
            <motion.div 
                              className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 sm:mb-6"
              variants={iconVariants}
              whileHover="hover"
            >
              <span className="text-xl sm:text-2xl">{service.icon}</span>
            </motion.div>

            <motion.h3 
              className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-3 sm:mb-4"
              whileHover={{ 
                color: "#8b5cf6",
                x: 5,
                transition: { duration: 0.2 }
              }}
            >
              {service.title}
            </motion.h3>

            <motion.p 
              className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed"
              initial={{ opacity: 0.8 }}
              whileHover={{ 
                opacity: 1,
                transition: { duration: 0.2 }
              }}
            >
              {service.description}
            </motion.p>

            <motion.div 
              className="mb-6"
              variants={containerVariants}
            >
              <h4 className="text-sm font-semibold text-gray-900 mb-3">What's included:</h4>
              <ul className="space-y-2">
                {service.features.map((feature, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center text-sm text-gray-600"
                    variants={featureVariants}
                    whileHover={{ 
                      x: 5,
                      color: "#374151",
                      transition: { duration: 0.2 }
                    }}
                  >
                    <motion.span 
                                              className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-3"
                      whileHover={{ scale: 1.5 }}
                    />
                    {feature}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="pt-6 border-t border-gray-100"
                              whileHover={{ borderColor: "#8b5cf6" }}
              transition={{ duration: 0.3 }}
            >
              <motion.div 
                className="flex justify-center"
                whileHover={{ scale: 1.02 }}
              >
                <motion.button
                  className="px-6 py-3 bg-gradient-dark text-white rounded-lg text-sm font-medium glow-on-hover"
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: "#8b5cf6",
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Quote
                </motion.button>
              </motion.div>
            </motion.div>
          </motion.div>
        ))}
      </motion.div>

      {/* CTA Section */}
      <motion.div 
                  className="text-center mt-16 p-8 bg-gradient-soft rounded-2xl shadow-lg"
        variants={cardVariants}
        whileHover={{ 
          scale: 1.01,
          transition: { duration: 0.3 }
        }}
      >
        <motion.h3 
          className="text-xl sm:text-2xl font-bold text-gray-900 mb-4"
          whileHover={{ scale: 1.02 }}
        >
          Ready to Grow Your Shopify Store?
        </motion.h3>
        <motion.p 
          className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto"
          initial={{ opacity: 0.8 }}
          whileHover={{ opacity: 1 }}
        >
          Let's discuss your project and create a custom solution that drives results. 
          Get a free consultation and project quote.
        </motion.p>
        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center">
          <motion.a
            href="mailto:haris.butt0014@gmail.com"
            className="btn-gradient inline-flex items-center mr-4"
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            Start Project
            <motion.svg 
              className="ml-2 w-4 h-4" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              initial={{ x: 0 }}
              whileHover={{ 
                x: 3,
                transition: { duration: 0.2 }
              }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </motion.svg>
          </motion.a>
          <motion.button
            onClick={() => {
              setActiveTab("Projects");
              // Smooth scroll to top of the page
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }}
            className="btn-gradient-soft inline-flex items-center ml-4"
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            View Projects
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
} 